/**
 * React Query cache keys, in one dependency-free module.
 *
 * Split out of the hook modules because keys are needed by code that has no
 * business loading a hook. `import-configs.tsx` is the case that forced it: it
 * declares which caches each import type invalidates, so it needs the keys — but
 * importing them from `@/hooks/useBanking` pulled in the whole hook module (and
 * through it the supabase client and the mock banking data) merely to read three
 * arrays. The visible cost was test-side: two unrelated page tests had to grow
 * `BANKING_KEYS` / `PROJECTION_KEYS` mocks to keep the config file loadable
 * (DCP-298 review).
 *
 * This module imports types only, so it stays free of runtime dependencies. Keep
 * it that way — a value import here would put whatever it pulls in back on the
 * path of everything that needs a key.
 *
 * The hook modules re-export their own keys for compatibility; new code should
 * import from here.
 */

// TYPE-ONLY, and that is what keeps the "dependency-free" claim above true:
// `import type` is erased at compile time, so nothing from db-tenant is
// pulled into the bundle by the modules that only need a key.
import type { CreditCardAvailabilityQuery, FacilitiesPageQuery, PacksPageQuery } from '@/lib/db-tenant'
import type { BankingFilters, CrossCheckingTab, LocTxnScope, ManualTransactionFilters } from '@/types/banking'
import type { ProjectionFilters } from '@/lib/supabase'
import type { CreditCardTransactionsQuery } from '@/lib/credit-card-transactions-db'

/**
 * Query keys for banking data
 * All keys include organizationId to ensure proper cache isolation
 */
export const BANKING_KEYS = {
  // Prefixes for broad invalidation (match all orgs/filters)
  allManualTransactions: ['banking', 'manual-transactions'] as const,
  allManualTransactionCounts: ['banking', 'manual-transaction-count'] as const,
  allBankAccounts: ['banking', 'bank-accounts'] as const,
  allBalanceImportStatus: ['banking', 'balance-import-status'] as const,
  allKpi: ['banking', 'kpi'] as const,
  // Reaches BOTH cross-checking queries (`crossCheckingKpi` and
  // `crossCheckingTransactions`) — they are the only keys rooted at
  // 'cross-checking', and both read the `transactions` + `balances` rows that a
  // LedgerSync disconnect deletes (DCP-338).
  allCrossChecking: ['cross-checking'] as const,

  // The three below exist for `FileImportConfig.invalidateKeys`, which is a
  // static array built at module scope where no organization id is in hand. The
  // org-scoped factories further down cannot be called from there, and React
  // Query matches keys by prefix, so these reach every org's cached entry —
  // the same trade-off the prefixes above already make. Harmless in practice:
  // invalidation only marks entries stale, and just mounted observers refetch,
  // of which there is at most one org's worth at a time (DCP-298).
  allLocAvailability: ['banking', 'loc-availability'] as const,
  allLocTransactions: ['banking', 'loc-transactions'] as const,
  allFacilityCategories: ['banking', 'facility-categories'] as const,

  kpi: (organizationId: string | undefined, filters?: BankingFilters) => ['banking', 'kpi', organizationId, filters] as const,
  facilityCategories: (organizationId: string | undefined, startDate?: string, endDate?: string) => ['banking', 'facility-categories', organizationId, startDate, endDate] as const,
  importStatus: (organizationId: string | undefined) => ['banking', 'import-status', organizationId] as const,
  balanceImportStatus: (
    organizationId: string | undefined,
    page?: number,
    pageSize?: number,
    facilityIds?: string[],
  ) => ['banking', 'balance-import-status', organizationId, page, pageSize, facilityIds] as const,
  facilities: (organizationId: string | undefined) => ['banking', 'facilities', organizationId] as const,
  packs: (organizationId: string | undefined) => ['banking', 'packs', organizationId] as const,
  /**
   * One page of the Settings → Facilities table (filters + page), paged on the
   * server. Under {@link BANKING_KEYS.facilities} so every facility write that
   * invalidates the list refreshes the table too; slot 2 is the organization.
   */
  facilitiesPage: (organizationId: string | undefined, query: FacilitiesPageQuery) =>
    ['banking', 'facilities', organizationId, 'page', query] as const,
  /**
   * One page of the Settings → Packs table, paged on the server. Under
   * {@link BANKING_KEYS.packs} so every pack write refreshes it; slot 2 is the
   * organization.
   */
  packsPage: (organizationId: string | undefined, query: PacksPageQuery) =>
    ['banking', 'packs', organizationId, 'page', query] as const,
  locAvailability: (organizationId: string | undefined) => ['banking', 'loc-availability', organizationId] as const,
  // The org-wide key stays a 3-element key ON PURPOSE (DCP-333). Six mutations
  // invalidate with `locTransactions(orgId)` and rely on React Query's PREFIX
  // matching to reach every scoped entry below. Giving this helper an optional
  // 4th element would make it produce `[…, orgId, undefined]`, which is NOT a
  // prefix of `[…, orgId, { kind, id }]` — every one of those invalidations
  // would then match nothing and silently stop refreshing the drill-downs.
  locTransactions: (organizationId: string | undefined) => ['banking', 'loc-transactions', organizationId] as const,
  /** Per-pop-up key: one cache entry per drilled line/facility/pack. */
  locTransactionsScoped: (organizationId: string | undefined, scope: LocTxnScope | null) =>
    ['banking', 'loc-transactions', organizationId, scope] as const,
  bankAccounts: (organizationId: string | undefined) => ['banking', 'bank-accounts', organizationId] as const,
  /**
   * Live bank accounts per facility for the given facilities (one table page).
   * Under {@link BANKING_KEYS.bankAccounts} so every account write that
   * invalidates the account lists refreshes the counts too.
   */
  facilityBankAccountCounts: (organizationId: string | undefined, facilityIds: readonly string[]) =>
    ['banking', 'bank-accounts', organizationId, 'facility-counts', facilityIds] as const,

  // --- DCP-374: Credit Card Availability ---
  /**
   * The whole query shape (filters, sort, page, size) is part of the key
   * because the table is paged SERVER-side: each combination is its own result
   * set with its own total. Slot 2 is the organization id, which the hook's
   * placeholder checks so a previous answer never outlives an org switch.
   */
  creditCardAvailability: (
    organizationId: string | undefined,
    query: CreditCardAvailabilityQuery,
  ) => ['banking', 'credit-card-availability', organizationId, query] as const,
  /** Prefix for broad invalidation — reaches every filter/page combination. */
  allCreditCardAvailability: ['banking', 'credit-card-availability'] as const,
  /**
   * One page of one card's transactions for one date range ("View
   * transactions"): range, page and size are all in the query, because the
   * dialog pages server-side. A sibling of the card table, not under it, so
   * opening or paging the dialog never refetches the table; transaction
   * writes invalidate both prefixes (the table's `has_transactions` gates the
   * action). Slot 2 is the organization id, which the hook's
   * placeholder checks.
   */
  creditCardTransactions: (organizationId: string | undefined, query: CreditCardTransactionsQuery | null) =>
    ['banking', 'credit-card-transactions', organizationId, query] as const,
  /** Prefix for broad invalidation — every card, range and organization. */
  allCreditCardTransactions: ['banking', 'credit-card-transactions'] as const,

  // --- DCP-324: Manage Bank Connections ---
  /** Prefix reaching every org's connection list, for broad invalidation. */
  allBankConnections: ['banking', 'bank-connections'] as const,
  /** Prefix reaching every connection's accounts table, any filter. */
  allConnectionAccounts: ['banking', 'connection-accounts'] as const,

  /**
   * The connection list. Keyed by sort because the RPC sorts server-side, so
   * two sort orders are genuinely different responses rather than two views of
   * one cached array.
   */
  bankConnections: (
    organizationId: string | undefined,
    sortColumn?: string,
    sortDirection?: string,
  ) => ['banking', 'bank-connections', organizationId, sortColumn, sortDirection] as const,

  /**
   * Per-connection accounts, ORG+CONNECTION scoped and deliberately 4 elements
   * long — it is the invalidation handle the mutations use, and React Query
   * matches by PREFIX. Every filtered/sorted entry lives under
   * {@link BANKING_KEYS.connectionAccountsScoped}, which appends a 5th element,
   * so invalidating this key reaches all of them.
   *
   * Do NOT give this helper an optional 5th parameter. It would produce
   * `[…, connectionId, undefined]`, which is NOT a prefix of
   * `[…, connectionId, query]` — every mutation's invalidation would then match
   * nothing and the table would silently stop refreshing after a rename,
   * reassignment or disconnect. This is the exact bug DCP-333 fixed on
   * `locTransactions`; the same shape is kept here for the same reason.
   */
  connectionAccounts: (organizationId: string | undefined, connectionId: string) =>
    ['banking', 'connection-accounts', organizationId, connectionId] as const,

  /**
   * One cache entry per (filter, sort, page, page size) of a connection's
   * accounts table: the table pages on the SERVER, so each combination is its
   * own result with its own total (see `useConnectionAccounts`). Under
   * {@link BANKING_KEYS.connectionAccounts}, so the mutations' prefix
   * invalidation reaches every page. Slot 2 is the organization id, which the
   * hook's placeholder checks.
   */
  connectionAccountsScoped: (
    organizationId: string | undefined,
    connectionId: string,
    query: Record<string, unknown> | undefined,
  ) => ['banking', 'connection-accounts', organizationId, connectionId, query] as const,

  /** Prefix reaching every facility's per-connection account counts, for broad invalidation. */
  allFacilityConnectionAccountCounts: ['banking', 'facility-connection-account-counts'] as const,
  /**
   * How many live accounts each connection has at one facility — the facility
   * page's "Accounts & cards" figure, which the org-wide connection list
   * cannot carry.
   */
  facilityConnectionAccountCounts: (organizationId: string | undefined, facilityId: string) =>
    ['banking', 'facility-connection-account-counts', organizationId, facilityId] as const,
  /**
   * A connection's accounts with no facility, for "Add account to {Facility}".
   * Under {@link BANKING_KEYS.connectionAccounts} so every write that
   * invalidates the connection's accounts refreshes it too.
   */
  unassignedConnectionAccounts: (organizationId: string | undefined, connectionId: string) =>
    ['banking', 'connection-accounts', organizationId, connectionId, 'unassigned'] as const,

  manualTransactions: (organizationId: string | undefined, filters?: ManualTransactionFilters) => ['banking', 'manual-transactions', organizationId, filters] as const,
  manualTransactionCount: (organizationId: string | undefined, filters?: ManualTransactionFilters) => ['banking', 'manual-transaction-count', organizationId, filters] as const,

  crossCheckingKpi: (organizationId: string | undefined, filters?: BankingFilters) => ['cross-checking', 'kpi', organizationId, filters] as const,
  crossCheckingTransactions: (organizationId: string | undefined, filters?: BankingFilters, tab?: CrossCheckingTab) => ['cross-checking', 'transactions', organizationId, filters, tab] as const,

  allFileImports: ['banking', 'file-imports'] as const,
  // Prefix of every file import's transaction list (all orgs, imports, filters).
  allFileImportTransactions: ['banking', 'file-import-transactions'] as const,
  // Prefix of every file import's projection list and count (all orgs, imports, filters).
  allFileImportProjections: ['banking', 'file-import-projections'] as const,
  fileImports: (organizationId: string | undefined, filters?: { status?: string }) => ['banking', 'file-imports', organizationId, filters] as const,
  fileImportById: (organizationId: string | undefined, id: string) => ['banking', 'file-import', organizationId, id] as const,
  fileImportTransactions: (orgId: string | undefined, fileImportId: string, filters?: Record<string, unknown>) =>
    ['banking', 'file-import-transactions', orgId, fileImportId, filters] as const,
  fileImportProjections: (orgId: string | undefined, fileImportId: string, filters?: Record<string, unknown>) =>
    ['banking', 'file-import-projections', orgId, fileImportId, filters] as const,
}

/**
 * Query keys for projections data
 * All keys include organizationId to ensure proper cache isolation
 *
 * Exported so non-mutation writers can invalidate the same cache. The file
 * importer is one: it inserts projections server-side (Trigger.dev) rather
 * than through a mutation here, so it has to invalidate `all` itself once the
 * background job reports `completed` (DCP-298).
 */
export const PROJECTION_KEYS = {
  all: ['projections'] as const,
  lists: () => [...PROJECTION_KEYS.all, 'list'] as const,
  list: (organizationId: string | undefined, filters?: ProjectionFilters) =>
    [...PROJECTION_KEYS.lists(), organizationId, filters] as const,
  /** One Cash Position cell; under `all`, so every projection write invalidates it. */
  cell: (
    organizationId: string | undefined,
    facilityId: string,
    start: string,
    end: string,
    categoryIds: readonly string[],
  ) => [...PROJECTION_KEYS.all, 'cell', organizationId, facilityId, start, end, categoryIds] as const,
}

/** Query-key prefix for all Cash Position Summary queries. Invalidate this
 *  whenever data feeding the summary changes (projections, balances, bank
 *  accounts, facilities, sage checks). */
export const CASH_POSITION_SUMMARIES_KEY = ['cash-position-summaries'] as const

/**
 * `placeholderData` for a paged/filtered query: keep the previous answer on
 * screen while the next key loads (the table and its footer stay mounted), but
 * never across an organization switch — another org's rows, with live row
 * actions, must not stand in for this one's.
 *
 * @param organizationId - the org of the query being rendered
 * @param orgSlot - index of the organization id in the query's key, as laid out
 *   by its factory (e.g. 2 for `BANKING_KEYS.fileImports`, 1 for `SAGE_KEYS.checks`)
 */
export function keepPreviousForOrg(organizationId: string | undefined, orgSlot: number) {
  return <T>(previous: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined): T | undefined =>
    previousQuery?.queryKey[orgSlot] === organizationId ? previous : undefined
}
